import axios from '@/plugins/axios'

export default {
  namespaced: true,

  state: {
    items: [],
    total: 0,
    pages: 0,
    page: 1,
    perPage: 10,
    search: '',
    department: '',
    workStatus: '',
    safetyStatus: null,
    loading: false,
    error: null,
    personnel: null
  },

  mutations: {
    SET_ITEMS(state, data) {
      // Handle both array and {items: []} formats
      state.items = Array.isArray(data) ? data : (data?.items || [])
    },
    SET_PAGINATION(state, { total, pages, page, perPage }) {
      state.total = total
      state.pages = pages 
      state.page = page
      state.perPage = perPage
    },
    SET_SEARCH(state, search) {
      state.search = search
    },
    SET_DEPARTMENT(state, department) {
      state.department = department
    },
    SET_WORK_STATUS(state, status) {
      state.workStatus = status
    },
    SET_SAFETY_STATUS(state, status) {
      state.safetyStatus = status
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    SET_PERSONNEL(state, personnel) {
      state.personnel = personnel
    },
    RESET(state) {
      state.items = []
      state.total = 0
      state.pages = 0
      state.page = 1
      state.perPage = 10
      state.search = ''
      state.department = ''
      state.workStatus = ''
      state.safetyStatus = null
      state.loading = false
      state.error = null
      state.personnel = null
    }
  },

  getters: {
    getAllPersonnel: state => state.items
  },

  actions: {
    // Fetch with pagination
    async fetchPersonnel({ commit, state }) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const params = {
          page: state.page,
          per_page: state.perPage,
          search: state.search,
          department: state.department || undefined,
          work_status: state.workStatus || undefined,
          safety_status: state.safetyStatus
        }

        const response = await axios.get('/personnel', { params })
        const data = response.data
        
        if (data.items && data.total) {
          // Paginated response
          commit('SET_ITEMS', data.items)
          commit('SET_PAGINATION', {
            total: data.total,
            pages: data.pages,
            page: data.page,
            perPage: data.per_page
          })
        } else {
          // Direct array response
          commit('SET_ITEMS', data)
          commit('SET_PAGINATION', {
            total: data.length,
            pages: 1,
            page: 1,
            perPage: data.length
          })
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Có lỗi xảy ra')
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Fetch all personnel without pagination
    async fetchAllPersonnel({ commit }) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await axios.get('/personnel', {
          params: { per_page: -1 } // Special flag to get all items
        })
        
        commit('SET_ITEMS', response.data.items || [])
        return response.data.items
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Có lỗi xảy ra')
        return []
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchPersonnelById({ commit }, id) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await axios.get(`/personnel/${id}`)
        commit('SET_PERSONNEL', response.data)
        return response.data
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Có lỗi xảy ra')
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async createPersonnel({ dispatch }, data) {
      try {
        await axios.post('/personnel', data)
        dispatch('fetchPersonnel')
        return { success: true }
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.message || 'Có lỗi xảy ra'
        }
      }
    },

    async updatePersonnel({ dispatch }, { id, data }) {
      try {
        await axios.put(`/personnel/${id}`, data)
        dispatch('fetchPersonnel')
        return { success: true }
      } catch (error) {
        return {
          success: false, 
          error: error.response?.data?.message || 'Có lỗi xảy ra'
        }
      }
    },

    async deletePersonnel({ dispatch }, id) {
      try {
        await axios.delete(`/personnel/${id}`)
        dispatch('fetchPersonnel')
        return { success: true }
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.message || 'Có lỗi xảy ra'
        }
      }
    },

    async importPersonnel({ dispatch }, file) {
      try {
        const formData = new FormData()
        formData.append('file', file)
        
        await axios.post('/personnel/import', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        
        dispatch('fetchPersonnel')
        return { success: true }
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.message || 'Có lỗi xảy ra'
        }
      }
    },

    async exportPersonnel() {
      try {
        const response = await axios.get('/personnel/export', {
          responseType: 'blob'
        })

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        const filename = response.headers['content-disposition']?.split('filename=')[1] || 'personnel.xlsx'
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        return { success: true }
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.message || 'Có lỗi xảy ra'
        }
      }
    },

    async uploadFaceImage(_, { id, data }) {
      try {
        const response = await axios.post(`/personnel/${id}/face-image`, data, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        return {
          success: true,
          url: response.data.url
        }
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.message || 'Không thể tải lên ảnh'
        }
      }
    }
  }
}